// Declarative single source of truth for the staff sidebar. The sidebar
// renders purely from this config; favourites + collapse persist nav-config
// `id`s (stable, kebab-case, independent of `href` so a route can change
// without orphaning a saved favourite). Regroups the previously-flat list
// into logical sections — renames/merges nothing (every label + route is
// unchanged from the pre-refactor sidebar).
//
// Spec: docs/superpowers/specs/2026-06-07-sidebar-favourites-collapsible-design.md
// ⌘K / Jump to… (ADR-0078) uses the same inventory + role/flag filters.
import { roleCanAccess, type Role } from "@l30/shared";

export interface NavItem {
  id: string;
  href: string;
  label: string;
  /**
   * RESOURCE_PERMISSIONS key used by the ⌘K palette RBAC filter.
   * `null` = every authenticated staff role may see the destination
   * (dashboard / settings chrome — not a gated API resource).
   */
  resource: string | null;
  /** When set, sidebar/mobile nav hide the item unless this feature flag is on. */
  featureFlag?: string;
  /**
   * Extra ⌘K / Jump to… search terms (label alone is often too short —
   * e.g. Atlas is also "maps" / "hub" / "ideation maps").
   */
  keywords?: string[];
}

export interface NavSection {
  id: string;
  label: string;
  /** Home + Settings are non-collapsible (always rendered open, no chevron). */
  collapsible: boolean;
  items: NavItem[];
}

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "home",
    label: "Home",
    collapsible: false,
    items: [
      {
        id: "insights-home",
        href: "/",
        label: "Insights",
        resource: null,
        keywords: ["home", "search", "subjects", "intelligence hub", "entry"],
      },
      {
        id: "dashboard",
        href: "/dashboard",
        label: "Dashboard",
        resource: null,
        keywords: ["overview", "today", "modules"],
      },
      {
        id: "projects",
        href: "/projects",
        label: "Projects",
        resource: "projects",
        keywords: ["work", "delivery", "engagements"],
      },
      {
        id: "data-rooms",
        href: "/data-rooms",
        label: "Data rooms",
        resource: "projects",
        keywords: ["proforma", "entity", "investor", "diligence"],
      },
      {
        id: "my-tasks",
        href: "/tasks",
        label: "My tasks",
        resource: "tasks",
        keywords: ["todo", "to-do", "actions", "checklist"],
      },
    ],
  },
  {
    id: "crm-sales",
    label: "CRM & Sales",
    collapsible: true,
    items: [
      {
        id: "contacts",
        href: "/contacts",
        label: "Contacts",
        resource: "contacts",
        keywords: ["people", "crm", "person"],
      },
      {
        id: "groups",
        href: "/contact-groups",
        label: "Groups",
        resource: "contact-groups",
        keywords: ["contact groups", "lists", "segments"],
      },
      {
        id: "companies",
        href: "/companies",
        label: "Companies",
        resource: "companies",
        keywords: ["orgs", "organisations", "organizations", "accounts"],
      },
      // CRM deals/leads share the contacts gate on the API router.
      {
        id: "lead-queue",
        href: "/crm/leads",
        label: "Lead queue",
        resource: "contacts",
        keywords: ["leads", "inbound", "prospects", "enquiries"],
      },
      {
        id: "deals",
        href: "/crm/deals",
        label: "Deals",
        resource: "contacts",
        keywords: ["pipeline", "opportunities", "sales"],
      },
      {
        id: "campaigns",
        href: "/crm/campaigns",
        label: "Campaigns",
        resource: "crm-campaigns",
        keywords: ["marketing", "email campaigns", "outreach"],
      },
      {
        id: "attribution",
        href: "/crm/attribution",
        label: "Attribution",
        resource: "crm-campaigns",
        keywords: ["utm", "source", "campaign attribution"],
      },
    ],
  },
  {
    id: "documents-signing",
    label: "Documents & Signing",
    collapsible: true,
    items: [
      {
        id: "documents",
        href: "/documents",
        label: "Documents",
        resource: "documents",
        keywords: ["vault", "files", "working documents", "sharepoint"],
      },
      {
        id: "my-vault",
        href: "/my-vault",
        label: "My vault",
        resource: "documents",
        keywords: ["personal vault", "personal files", "my documents"],
      },
      {
        id: "board",
        href: "/board-meetings",
        label: "Board",
        resource: "board-meetings",
        keywords: ["board meetings", "pack", "minutes", "resolutions"],
      },
      {
        id: "signatures",
        href: "/signatures",
        label: "Signatures",
        resource: "signatures",
        keywords: ["sign", "nda", "e-sign", "esign", "contracts"],
      },
    ],
  },
  {
    id: "comms",
    label: "Comms",
    collapsible: true,
    items: [
      {
        id: "tracked-email",
        href: "/email",
        label: "Tracked Email",
        resource: "email",
        keywords: ["email", "mail", "opens", "clicks", "compose"],
      },
      {
        id: "messages",
        href: "/messages",
        label: "Messages",
        resource: "messages",
        keywords: ["chat", "channels", "dm", "slack"],
      },
      {
        id: "meetings",
        href: "/meetings",
        label: "Meetings",
        resource: "meetings",
        keywords: ["calendar meetings", "agenda"],
      },
      {
        id: "scheduling",
        href: "/scheduling",
        label: "Scheduling",
        resource: "scheduling",
        keywords: ["booking", "calendly", "availability", "slots"],
      },
    ],
  },
  {
    id: "finance",
    label: "Finance",
    collapsible: true,
    items: [
      {
        id: "cashflow",
        href: "/cashflow",
        label: "Cashflow",
        resource: "cashflow",
        keywords: ["qbo", "quickbooks", "money", "forecast"],
      },
      {
        id: "receipts",
        href: "/receipts",
        label: "Receipts",
        resource: "receipts",
        keywords: ["expenses", "claims", "reimbursement"],
      },
      {
        id: "time",
        href: "/time-tracking",
        label: "Time",
        resource: "time-tracking",
        keywords: ["timer", "timesheet", "hours", "billable"],
      },
    ],
  },
  {
    id: "workspace",
    // Atlas weight (ADR-0078): section label elevates ideation maps as a
    // first-class surface without removing other workspace destinations.
    label: "Atlas & Workspace",
    collapsible: true,
    items: [
      {
        id: "notes",
        href: "/notes",
        label: "Notes",
        resource: "notes",
        keywords: ["notebook", "capture", "parking lot"],
      },
      {
        id: "ask-insights",
        href: "/ai",
        label: "Ask Insights",
        resource: "ai",
        featureFlag: "insights_ai",
        keywords: ["ai", "assistant", "claude", "chat"],
      },
      {
        id: "ideation",
        href: "/ideation",
        label: "Ideation",
        resource: "ideation",
        // NOTE: /ideation is a path-prefix of /ideation/maps + /ideation/chat —
        // active-route matching must handle this overlap
        keywords: ["notes graph", "boards", "clusters", "classic ideation"],
      },
      {
        id: "ideation-maps",
        href: "/ideation/maps",
        label: "Atlas",
        resource: "ideation",
        keywords: ["maps", "hub", "ideation maps", "knowledge maps", "graph"],
      },
      {
        id: "ideation-chat",
        href: "/ideation/chat",
        label: "Ideation Chat",
        resource: "ideation",
        keywords: ["corpus chat", "ask across maps", "rag"],
      },
      {
        id: "authenticator",
        href: "/authenticator",
        label: "Authenticator",
        resource: "authenticator",
        keywords: ["totp", "2fa", "mfa", "otp", "passwords"],
      },
    ],
  },
  {
    id: "client-portal",
    label: "Client Portal",
    collapsible: true,
    items: [
      {
        id: "partner-vaults",
        href: "/partner-vaults",
        label: "Partner vaults",
        resource: "partner-vaults",
        keywords: ["client vault", "portal vault", "shared vault"],
      },
      {
        id: "portal-users",
        href: "/portal-users",
        label: "Portal users",
        resource: "portal-admin",
        keywords: [
          "guests",
          "magic link",
          "portal invites",
          "onboard",
          "onboarding",
          "new partner",
          "provision",
          "belsize",
          "wizard",
          "add user",
          "invite",
        ],
      },
      {
        id: "engagement-plan",
        href: "/engagement-plan",
        label: "Engagement plan",
        resource: "portal-admin",
        keywords: ["brief", "stakeholders", "milestones", "gop"],
      },
      {
        id: "programme-collaboration",
        href: "/programme-collaboration",
        label: "Committees & working groups",
        resource: "portal-admin",
        keywords: ["wg", "working groups", "committees", "programme", "bodies"],
      },
    ],
  },
  {
    id: "settings",
    label: "Settings & Usage",
    collapsible: false,
    items: [
      {
        id: "settings-page",
        href: "/settings",
        label: "Settings & Usage",
        resource: null,
        keywords: ["admin", "usage", "flags", "audit", "retention"],
      },
      {
        id: "settings-tokens",
        href: "/settings/tokens",
        label: "Personal Access Tokens",
        resource: "tokens",
        keywords: ["pat", "api token", "mcp", "claude desktop", "graph:write"],
      },
    ],
  },
];

/** ⌘K destinations visible to `role` — sidebar may stay full; palette hides dead links. */
export function filterNavSectionsForRole(role: Role): NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => item.resource === null || roleCanAccess(item.resource, role),
    ),
  })).filter((section) => section.items.length > 0);
}

/** Drop items gated by a feature flag that is off (or still loading → hide). */
export function filterNavSectionsForFlags(
  sections: NavSection[],
  enabledFlags: ReadonlySet<string>,
): NavSection[] {
  return sections
    .map((section) => ({
      ...section,
      items: section.items.filter(
        (item) => !item.featureFlag || enabledFlags.has(item.featureFlag),
      ),
    }))
    .filter((section) => section.items.length > 0);
}

/** First few keywords for a muted palette hint (empty when none). */
export function navItemHint(item: NavItem, max = 3): string {
  const terms = (item.keywords ?? []).slice(0, max);
  return terms.length > 0 ? terms.join(" · ") : "";
}
